import { AlertTriangle, CheckCircle, LucideIcon, Zap } from "lucide-react";
import React, { useEffect, useState } from "react";
import { useTheme } from "../contexts/ThemeContext";

export interface DramaticModalAction {
  text: string;
}

export interface DramaticConfirmationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  loading: boolean;
  
  // Content
  title: string;
  subtitle?: string;
  loadingTitle?: string;
  loadingSubtitle?: string;
  actions?: DramaticModalAction[];
  children?: React.ReactNode;
  
  // Styling
  mode: "positive" | "destructive" | "amber";
  icon?: LucideIcon;
  confirmText?: string;
  cancelText?: string;
  
  // Optional: show particles animation
  showParticles?: boolean;
}

const DramaticConfirmationModal: React.FC<DramaticConfirmationModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  loading,
  title,
  subtitle,
  loadingTitle,
  loadingSubtitle,
  actions = [],
  children,
  mode,
  icon: CustomIcon,
  confirmText,
  cancelText = "Cancel",
  showParticles = true,
}) => {
  const { theme } = useTheme();
  const isDarkMode = theme === "dark";
  const [showContent, setShowContent] = useState(false);
  const [loadingProgress, setLoadingProgress] = useState(0);

  const isPositive = mode === "positive" || mode === "amber";
  const isDestructive = mode === "destructive";
  
  // Default icon based on mode
  const IconComponent = CustomIcon || (isDestructive ? AlertTriangle : CheckCircle);
  
  // Default confirm text based on mode
  const finalConfirmText = confirmText || (isDestructive ? "Proceed" : "Confirm");

  const colorMap = {
    positive: {
      gradient: "from-green-500 via-emerald-500 to-green-500",
      gradientButton: "from-green-500 to-emerald-600",
      ringColor: "rgba(34, 197, 94, 0.3)",
      ringColorStrong: "rgba(34, 197, 94, 0.5)",
      glowColor: "rgba(34, 197, 94, 0.4)",
      glowRgba: "rgba(34, 197, 94, 0.5)",
      glowRgbaLight: "rgba(34, 197, 94, 0.3)",
      particle: "bg-green-400",
      infoBg: isDarkMode ? "bg-green-900/20 border-green-800/50" : "bg-green-50 border-green-200",
      infoIcon: isDarkMode ? "text-green-400" : "text-green-600",
      infoTitle: isDarkMode ? "text-green-300" : "text-green-800",
      infoText: isDarkMode ? "text-green-400/80" : "text-green-700",
      buttonHoverFrom: "#16a34a",
      buttonHoverTo: "#059669",
    },
    amber: {
      gradient: "from-amber-500 via-yellow-500 to-amber-500",
      gradientButton: "from-amber-500 to-yellow-600",
      ringColor: "rgba(245, 158, 11, 0.3)",
      ringColorStrong: "rgba(245, 158, 11, 0.5)",
      glowColor: "rgba(245, 158, 11, 0.4)",
      glowRgba: "rgba(245, 158, 11, 0.5)",
      glowRgbaLight: "rgba(245, 158, 11, 0.3)",
      particle: "bg-amber-400",
      infoBg: isDarkMode ? "bg-amber-900/20 border-amber-800/50" : "bg-amber-50 border-amber-200",
      infoIcon: isDarkMode ? "text-amber-400" : "text-amber-600",
      infoTitle: isDarkMode ? "text-amber-300" : "text-amber-800",
      infoText: isDarkMode ? "text-amber-400/80" : "text-amber-700",
      buttonHoverFrom: "#d97706",
      buttonHoverTo: "#ca8a04",
    },
    destructive: {
      gradient: "from-red-500 via-orange-500 to-red-500",
      gradientButton: "from-red-500 to-orange-600",
      ringColor: "rgba(239, 68, 68, 0.3)",
      ringColorStrong: "rgba(239, 68, 68, 0.5)",
      glowColor: "rgba(239, 68, 68, 0.4)",
      glowRgba: "rgba(239, 68, 68, 0.5)",
      glowRgbaLight: "rgba(239, 68, 68, 0.3)",
      particle: "bg-red-400",
      infoBg: isDarkMode ? "bg-red-900/20 border-red-800/50" : "bg-red-50 border-red-200",
      infoIcon: isDarkMode ? "text-red-400" : "text-red-600",
      infoTitle: isDarkMode ? "text-red-300" : "text-red-800",
      infoText: isDarkMode ? "text-red-400/80" : "text-red-700",
      buttonHoverFrom: "#dc2626",
      buttonHoverTo: "#ea580c",
    },
  };
  const colors = colorMap[mode];

  // Animate content entrance
  useEffect(() => {
    if (isOpen) {
      const timer = setTimeout(() => setShowContent(true), 50);
      return () => clearTimeout(timer);
    } else {
      setShowContent(false);
      setLoadingProgress(0);
    }
  }, [isOpen]);

  // Simulate progress during loading
  useEffect(() => {
    if (loading) {
      setLoadingProgress(0);
      const interval = setInterval(() => {
        setLoadingProgress((prev) => {
          if (prev >= 90) return prev;
          return prev + Math.random() * 15;
        });
      }, 200);
      return () => clearInterval(interval);
    } else {
      if (loadingProgress > 0) {
        setLoadingProgress(100);
      }
    }
  }, [loading, loadingProgress]);

  if (!isOpen) return null;

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget && !loading) {
      onClose();
    }
  };

  return (
    <div
      className={`fixed inset-0 flex items-center justify-center z-50 p-4 transition-all duration-300 ${
        showContent ? "bg-black/70 backdrop-blur-sm" : "bg-black/0"
      }`}
      onClick={handleBackdropClick}
    >
      {/* Animated background particles */}
      {showParticles && showContent && (
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          {[...Array(20)].map((_, i) => (
            <div
              key={i}
              className={`absolute w-1 h-1 ${colors.particle} rounded-full animate-ping opacity-30`}
              style={{
                left: `${Math.random() * 100}%`,
                top: `${Math.random() * 100}%`,
                animationDelay: `${Math.random() * 2}s`,
                animationDuration: `${2 + Math.random() * 2}s`,
              }}
            />
          ))}
        </div>
      )}

      {/* Modal container */}
      <div
        className={`relative w-full max-w-lg transform transition-all duration-500 ${
          showContent ? "scale-100 opacity-100 translate-y-0" : "scale-95 opacity-0 translate-y-4"
        }`}
      >
        {/* Glow effect behind modal */}
        <div
          className={`absolute -inset-1 rounded-2xl blur-xl transition-opacity duration-500 ${
            showContent ? "opacity-60" : "opacity-0"
          } bg-gradient-to-r ${colors.gradient}`}
          style={{
            animation: showContent ? "pulse 2s ease-in-out infinite" : "none",
          }}
        />

        {/* Modal content */}
        <div
          className={`relative rounded-2xl overflow-hidden ${
            isDarkMode ? "bg-gray-900" : "bg-white"
          } shadow-2xl`}
        >
          {/* Top gradient bar */}
          <div
            className={`h-1.5 bg-gradient-to-r ${colors.gradient}`}
            style={{
              backgroundSize: "200% 100%",
              animation: "shimmer 2s linear infinite",
            }}
          />

          {/* Header with animated icon */}
          <div className="pt-8 pb-4 px-8 text-center">
            {/* Animated icon container */}
            <div className="relative inline-flex items-center justify-center mb-6">
              {/* Outer pulsing rings */}
              <div
                className="absolute w-32 h-32 rounded-full border-2"
                style={{
                  borderColor: colors.ringColor,
                  animation: loading ? "ping 1s cubic-bezier(0, 0, 0.2, 1) infinite" : "pulse 2s ease-in-out infinite",
                }}
              />
              <div
                className="absolute w-24 h-24 rounded-full border-2"
                style={{
                  borderColor: colors.ringColorStrong,
                  animation: loading ? "ping 1s cubic-bezier(0, 0, 0.2, 1) infinite 0.2s" : "pulse 2s ease-in-out infinite 0.3s",
                }}
              />

              {/* Inner glow */}
              <div
                className="absolute w-20 h-20 rounded-full blur-md"
                style={{
                  backgroundColor: colors.glowColor,
                  animation: "pulse 1.5s ease-in-out infinite",
                }}
              />

              {/* Icon circle */}
              <div
                className={`relative w-20 h-20 rounded-full flex items-center justify-center bg-gradient-to-br ${colors.gradientButton} shadow-lg`}
                style={{
                  boxShadow: `0 0 30px ${colors.glowRgba}, 0 0 60px ${colors.glowRgbaLight}`,
                }}
              >
                {loading ? (
                  <Zap
                    className="w-10 h-10 text-white"
                    style={{
                      animation: "bounce 0.5s ease-in-out infinite",
                    }}
                  />
                ) : (
                  <IconComponent className="w-10 h-10 text-white" />
                )}
              </div>
            </div>

            {/* Title */}
            <h2
              className={`text-2xl font-bold mb-2 ${isDarkMode ? "text-white" : "text-gray-900"}`}
            >
              {loading ? (loadingTitle || title) : title}
            </h2>

            {/* Subtitle */}
            {(subtitle || loadingSubtitle) && (
              <p className={`text-sm ${isDarkMode ? "text-gray-400" : "text-gray-600"}`}>
                {loading ? (loadingSubtitle || subtitle) : subtitle}
              </p>
            )}
          </div>

          {/* Progress bar during loading */}
          {loading && (
            <div className="px-8 pb-4">
              <div
                className={`h-2 rounded-full overflow-hidden ${
                  isDarkMode ? "bg-gray-800" : "bg-gray-200"
                }`}
              >
                <div
                  className={`h-full rounded-full transition-all duration-300 bg-gradient-to-r ${colors.gradient}`}
                  style={{
                    width: `${loadingProgress}%`,
                    backgroundSize: "200% 100%",
                    animation: "shimmer 1s linear infinite",
                  }}
                />
              </div>
              <p
                className={`text-xs mt-2 text-center ${
                  isDarkMode ? "text-gray-500" : "text-gray-400"
                }`}
              >
                Processing request...
              </p>
            </div>
          )}

          {/* Actions/Info section */}
          {!loading && actions.length > 0 && (
            <div className="px-8 pb-6">
              <div
                className={`p-4 rounded-xl border ${colors.infoBg}`}
              >
                <div className="space-y-3">
                  <div className="flex items-start">
                    {isDestructive ? (
                      <AlertTriangle className={`w-5 h-5 mr-3 mt-0.5 flex-shrink-0 ${colors.infoIcon}`} />
                    ) : (
                      <Zap className={`w-5 h-5 mr-3 mt-0.5 flex-shrink-0 ${colors.infoIcon}`} />
                    )}
                    <div>
                      <p className={`text-sm font-medium ${colors.infoTitle}`}>
                        {isDestructive ? "Warning: This action will:" : "This action will:"}
                      </p>
                      <ul className={`mt-2 text-sm space-y-1 ${colors.infoText}`}>
                        {actions.map((action, index) => (
                          <li key={index}>{action.text}</li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Custom content slot */}
          {!loading && children && (
            <div className="px-8 pb-6">{children}</div>
          )}

          {/* Action buttons */}
          <div
            className={`px-8 py-6 flex items-center justify-end space-x-4 border-t ${
              isDarkMode ? "border-gray-800 bg-gray-900/50" : "border-gray-100 bg-gray-50/50"
            }`}
          >
            <button
              onClick={onClose}
              disabled={loading}
              className={`px-6 py-3 rounded-xl font-medium transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed ${
                isDarkMode
                  ? "text-gray-300 hover:text-white hover:bg-gray-800"
                  : "text-gray-600 hover:text-gray-900 hover:bg-gray-100"
              }`}
            >
              {cancelText}
            </button>
            <button
              onClick={onConfirm}
              disabled={loading}
              className={`relative px-8 py-3 rounded-xl font-semibold text-white transition-all duration-200 disabled:cursor-not-allowed overflow-hidden group bg-gradient-to-r ${colors.gradientButton} disabled:opacity-70 hover:brightness-110 hover:scale-[1.02] active:scale-[0.98]`}
              style={{
                boxShadow: loading ? "none" : `0 4px 20px ${colors.glowRgbaLight}`,
              }}
            >
              {/* Button shine effect */}
              <span
                className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-700"
              />
              <span className="relative flex items-center">
                {loading ? (
                  <>
                    <svg
                      className="animate-spin -ml-1 mr-2 h-5 w-5 text-white"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      />
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      />
                    </svg>
                    Processing...
                  </>
                ) : (
                  <>
                    <IconComponent className="w-5 h-5 mr-2" />
                    {finalConfirmText}
                  </>
                )}
              </span>
            </button>
          </div>
        </div>
      </div>

      {/* Custom keyframe animations */}
      <style>{`
        @keyframes shimmer {
          0% { background-position: 200% 0; }
          100% { background-position: -200% 0; }
        }
        @keyframes pulse {
          0%, 100% { opacity: 1; transform: scale(1); }
          50% { opacity: 0.7; transform: scale(1.05); }
        }
        @keyframes ping {
          75%, 100% { transform: scale(1.5); opacity: 0; }
        }
        @keyframes bounce {
          0%, 100% { transform: translateY(-10%); }
          50% { transform: translateY(10%); }
        }
      `}</style>
    </div>
  );
};

export default DramaticConfirmationModal;

